// Trimming the users' own recordings (2026-10-06): the edit as data, and the plain signal
// processing around it. Non-destructive: the original stays in R2; the edit ({start, end, cuts})
// is stored on the recording and a rendered copy is uploaded beside it (api/src/routes/
// recordings.ts). Never used on Peterson's clips or the show clips: those are not ours to edit.
//
//   - Edit math: normalise (bounds, cut merge/overlap), kept segments, edited duration, and the
//     time maps between the original and the edited audio.
//   - Silence at the start and end: frame energy against the recording's own noise floor, with a
//     small pre/post pad (SILENCE below), so the first consonant and the last vowel's tail stay.
//   - Loudness: the gain that brings a recording to a common speech level without clipping.
//   - Rendering: the kept segments joined with short fades (no clicks at the joints), gain applied.
//
// Pure TS (no DOM, no Node): runs in the browser, in the API (validation) and in vitest.
import type { Pcm } from './types';

export const EDIT_VERSION = 1;

/** One trim of a recording. Times in seconds on the ORIGINAL audio. */
export interface RecordingEdit {
  v: typeof EDIT_VERSION;
  /** Kept audio starts here… */
  start: number;
  /** …and ends here (≤ dur). */
  end: number;
  /** Removed spans inside [start, end], sorted, non-overlapping. */
  cuts: [number, number][];
  /** The original's duration as the editor decoded it. */
  dur: number;
  /** Changes on every save: the rendered copy is accepted only for the edit it renders. */
  rev: string;
}

/** The parts of an edit that are about time (what the editor manipulates). */
export interface EditSpans {
  start: number;
  end: number;
  cuts: [number, number][];
}

export const EDIT_LIMITS = {
  /** At least this much audio must remain. */
  minKeep: 0.1,
  /** A cut shorter than this is dropped (a stray click of the handle). */
  minCut: 0.02,
  /** Cuts closer than this are merged into one. */
  mergeGap: 0.01,
  maxCuts: 50,
  /** Longest recording the API accepts an edit for (seconds). */
  maxDur: 600,
} as const;

const ms = (x: number) => Math.round(x * 1000) / 1000;

/**
 * Clamp an edit to [0, dur], order and merge its cuts, drop cuts that are too short or outside
 * the kept range, and pull start/end inward over cuts that touch them. Null when less than
 * EDIT_LIMITS.minKeep would remain, or the numbers are not finite.
 */
export function normalizeEdit(raw: EditSpans, dur: number): EditSpans | null {
  if (![raw.start, raw.end, dur].every(Number.isFinite) || dur <= 0) return null;
  let start = Math.max(0, Math.min(raw.start, dur));
  let end = Math.max(0, Math.min(raw.end, dur));
  if (end < start) [start, end] = [end, start];
  const spans = (raw.cuts ?? [])
    .filter((c) => Array.isArray(c) && c.length === 2 && Number.isFinite(c[0]) && Number.isFinite(c[1]))
    .map(([a, b]) => (a <= b ? [a, b] : [b, a]) as [number, number])
    .map(([a, b]) => [Math.max(a, start), Math.min(b, end)] as [number, number])
    .filter(([a, b]) => b - a > 0)
    .sort((x, y) => x[0] - y[0]);
  const merged: [number, number][] = [];
  for (const c of spans) {
    const last = merged[merged.length - 1];
    if (last && c[0] <= last[1] + EDIT_LIMITS.mergeGap) last[1] = Math.max(last[1], c[1]);
    else merged.push([c[0], c[1]]);
  }
  let cuts = merged.filter(([a, b]) => b - a >= EDIT_LIMITS.minCut);
  // A cut that reaches the start or the end is the same as moving that handle.
  while (cuts.length && cuts[0]![0] <= start + EDIT_LIMITS.mergeGap) {
    start = Math.max(start, cuts[0]![1]);
    cuts = cuts.slice(1);
  }
  while (cuts.length && cuts[cuts.length - 1]![1] >= end - EDIT_LIMITS.mergeGap) {
    end = Math.min(end, cuts[cuts.length - 1]![0]);
    cuts = cuts.slice(0, -1);
  }
  if (cuts.length > EDIT_LIMITS.maxCuts) return null;
  const out: EditSpans = { start: ms(start), end: ms(end), cuts: cuts.map(([a, b]) => [ms(a), ms(b)]) };
  return editedDuration(out) >= EDIT_LIMITS.minKeep ? out : null;
}

/** The audio that stays, in order: [start, cut0.a], [cut0.b, cut1.a], …, [cutN.b, end]. */
export function keptSegments(e: EditSpans): [number, number][] {
  const out: [number, number][] = [];
  let at = e.start;
  for (const [a, b] of e.cuts) {
    if (a > at) out.push([at, a]);
    at = Math.max(at, b);
  }
  if (e.end > at) out.push([at, e.end]);
  return out;
}

export function editedDuration(e: EditSpans): number {
  return keptSegments(e).reduce((s, [a, b]) => s + (b - a), 0);
}

/** Nothing removed (within a millisecond): the edit is the same as the original. */
export function isIdentity(e: EditSpans, dur: number): boolean {
  return e.start <= 0.001 && e.end >= dur - 0.001 && e.cuts.length === 0;
}

export interface MappedTime {
  /** Time in the edited audio. */
  t: number;
  /** The original time was in removed audio; `t` is then the cut point (where the audio resumes). */
  removed: boolean;
}

/**
 * Original time → edited time. A time inside removed audio (before start, in a cut, after end)
 * snaps to the cut point: the place in the edited audio where that gap now is. This is the
 * policy for anything placed on a recording's timeline (a syllable found by the stress check, a
 * playhead): it stays at the cut point, flagged `removed`, and is never silently moved elsewhere.
 */
export function toEdited(t: number, e: EditSpans): MappedTime {
  let acc = 0;
  for (const [a, b] of keptSegments(e)) {
    if (t < a) return { t: ms(acc), removed: true };
    if (t <= b) return { t: ms(acc + (t - a)), removed: false };
    acc += b - a;
  }
  return { t: ms(acc), removed: true };
}

/** Edited time → original time (the start of the next kept segment at a joint). */
export function toOriginal(t: number, e: EditSpans): number {
  const segs = keptSegments(e);
  let acc = 0;
  for (const [a, b] of segs) {
    if (t < acc + (b - a)) return ms(a + Math.max(0, t - acc));
    acc += b - a;
  }
  const last = segs[segs.length - 1];
  return last ? last[1] : e.start;
}

/** Add a removed span (merged with what it touches); null when it would leave too little. */
export function addCut(e: EditSpans, a: number, b: number, dur: number): EditSpans | null {
  return normalizeEdit({ ...e, cuts: [...e.cuts, [Math.min(a, b), Math.max(a, b)]] }, dur);
}

export function removeCut(e: EditSpans, index: number, dur: number): EditSpans | null {
  return normalizeEdit({ ...e, cuts: e.cuts.filter((_, i) => i !== index) }, dur);
}

/**
 * Validate an edit from the network (the API): the right version, finite numbers in range,
 * already normalised (the app sends normalizeEdit's output). Returns the cleaned edit or a reason.
 */
export function checkEdit(raw: unknown): { ok: true; edit: RecordingEdit } | { ok: false; reason: string } {
  if (!raw || typeof raw !== 'object') return { ok: false, reason: 'edit must be an object' };
  const r = raw as Record<string, unknown>;
  if (r.v !== EDIT_VERSION) return { ok: false, reason: `edit.v must be ${EDIT_VERSION}` };
  const dur = r.dur;
  if (typeof dur !== 'number' || !Number.isFinite(dur) || dur <= 0 || dur > EDIT_LIMITS.maxDur) return { ok: false, reason: 'edit.dur out of range' };
  if (typeof r.start !== 'number' || typeof r.end !== 'number') return { ok: false, reason: 'edit.start and edit.end must be numbers' };
  if (!Array.isArray(r.cuts) || r.cuts.length > EDIT_LIMITS.maxCuts) return { ok: false, reason: 'edit.cuts must be a list' };
  if (!r.cuts.every((c) => Array.isArray(c) && c.length === 2 && c.every((x) => typeof x === 'number')))
    return { ok: false, reason: 'edit.cuts must be [a, b] pairs' };
  if (typeof r.rev !== 'string' || !/^[A-Za-z0-9_-]{4,40}$/.test(r.rev)) return { ok: false, reason: 'edit.rev must be a short id' };
  const n = normalizeEdit({ start: r.start, end: r.end, cuts: r.cuts as [number, number][] }, dur);
  if (!n) return { ok: false, reason: 'the edit leaves too little audio' };
  return { ok: true, edit: { v: EDIT_VERSION, ...n, dur: ms(dur), rev: r.rev } };
}

// ---------- silence at the start and end ----------

export const SILENCE = {
  /** Analysis frame (seconds). */
  frame: 0.01,
  /** Speech is this many dB above the recording's noise floor… */
  overFloorDb: 12,
  /** …and not quieter than this many dB under its loudest frame. */
  underPeakDb: 35,
  /** At least this long above the threshold to count (a click is not speech). */
  minSpeech: 0.04,
  /** Kept before the first speech (a soft onset like h- or s- starts under the threshold). */
  padBefore: 0.12,
  /** Kept after the last speech (a vowel's tail, a final -s). */
  padAfter: 0.18,
} as const;

function frameDb(samples: Float32Array, rate: number): Float32Array {
  const n = Math.max(1, Math.round(SILENCE.frame * rate));
  const frames = Math.floor(samples.length / n);
  const out = new Float32Array(frames);
  for (let f = 0; f < frames; f++) {
    let s = 0;
    for (let i = f * n; i < (f + 1) * n; i++) s += samples[i]! * samples[i]!;
    out[f] = 10 * Math.log10(s / n + 1e-12);
  }
  return out;
}

function percentile(x: Float32Array, p: number): number {
  const s = Array.from(x).sort((a, b) => a - b);
  return s.length ? s[Math.min(s.length - 1, Math.max(0, Math.floor(p * (s.length - 1))))]! : -120;
}

/**
 * Where the speech in a recording starts and ends (seconds, padded and clamped), from frame energy
 * against the recording's own noise floor. Null when nothing stands out from the floor (silence,
 * or noise all the way through): the editor then leaves the handles where they are.
 */
export function detectSpeech(pcm: Pcm): { start: number; end: number } | null {
  const { samples, sampleRate: rate } = pcm;
  const db = frameDb(samples, rate);
  if (db.length < 3) return null;
  const floor = percentile(db, 0.1);
  const peak = percentile(db, 1);
  const threshold = Math.max(floor + SILENCE.overFloorDb, peak - SILENCE.underPeakDb);
  if (peak < floor + SILENCE.overFloorDb) return null;
  const need = Math.max(1, Math.round(SILENCE.minSpeech / SILENCE.frame));
  let first = -1;
  let last = -1;
  let run = 0;
  for (let f = 0; f < db.length; f++) {
    run = db[f]! >= threshold ? run + 1 : 0;
    if (run >= need) {
      if (first < 0) first = f - need + 1;
      last = f;
    }
  }
  if (first < 0) return null;
  // the run that ends the speech: extend `last` to the end of its above-threshold stretch
  while (last + 1 < db.length && db[last + 1]! >= threshold) last++;
  const dur = samples.length / rate;
  const start = Math.max(0, first * SILENCE.frame - SILENCE.padBefore);
  const end = Math.min(dur, (last + 1) * SILENCE.frame + SILENCE.padAfter);
  return { start: ms(start), end: ms(end) };
}

// ---------- loudness ----------

export const LOUDNESS = {
  /** The common speech level (RMS of the speech frames, dBFS). */
  targetDb: -20,
  /** Peaks never go above this after the gain (no clipping). */
  ceiling: 0.89,
  minGain: 0.1,
  maxGain: 8,
} as const;

export interface Loudness {
  /** RMS of the speech frames (dBFS); -120 for silence. */
  speechDb: number;
  /** Largest absolute sample. */
  peak: number;
  /** Gain to LOUDNESS.targetDb, limited so the peak stays under the ceiling. */
  gain: number;
}

export function measureLoudness(pcm: Pcm): Loudness {
  const { samples } = pcm;
  let peak = 0;
  for (let i = 0; i < samples.length; i++) peak = Math.max(peak, Math.abs(samples[i]!));
  const db = frameDb(samples, pcm.sampleRate);
  if (!db.length || peak === 0) return { speechDb: -120, peak, gain: 1 };
  const floor = percentile(db, 0.1);
  const loud = percentile(db, 1);
  const threshold = Math.max(floor + SILENCE.overFloorDb, loud - SILENCE.underPeakDb);
  let s = 0;
  let n = 0;
  for (const d of db) {
    if (d >= threshold) {
      s += 10 ** (d / 10);
      n++;
    }
  }
  const speechDb = n ? 10 * Math.log10(s / n) : 10 * Math.log10(Array.from(db).reduce((a, d) => a + 10 ** (d / 10), 0) / db.length);
  const want = 10 ** ((LOUDNESS.targetDb - speechDb) / 20);
  const gain = Math.min(want, LOUDNESS.ceiling / peak);
  return { speechDb: Math.round(speechDb * 10) / 10, peak, gain: Math.round(Math.min(LOUDNESS.maxGain, Math.max(LOUDNESS.minGain, gain)) * 1000) / 1000 };
}

// ---------- rendering and the waveform ----------

/** Fade at every joint and at both ends (seconds): no click where audio was cut. */
export const JOINT_FADE = 0.006;

/** The edited audio: the kept segments joined with short fades, times `gain` (clamped to ±1). */
export function renderEdit(pcm: Pcm, e: EditSpans, gain = 1): Pcm {
  const rate = pcm.sampleRate;
  const segs = keptSegments(e).map(([a, b]) => [Math.round(a * rate), Math.min(pcm.samples.length, Math.round(b * rate))] as const);
  const total = segs.reduce((s, [a, b]) => s + Math.max(0, b - a), 0);
  const out = new Float32Array(total);
  const fade = Math.max(1, Math.round(JOINT_FADE * rate));
  let o = 0;
  for (const [a, b] of segs) {
    const len = Math.max(0, b - a);
    const f = Math.min(fade, Math.floor(len / 2));
    for (let i = 0; i < len; i++) {
      let g = gain;
      if (i < f) g *= i / f;
      else if (i >= len - f) g *= (len - 1 - i) / f;
      out[o + i] = Math.max(-1, Math.min(1, pcm.samples[a + i]! * g));
    }
    o += len;
  }
  return { samples: out, sampleRate: rate };
}

/** Peak per bucket (0…1) for drawing a waveform: `buckets` bars over the whole recording. */
export function waveformPeaks(samples: Float32Array, buckets: number): Float32Array {
  const n = Math.max(1, Math.floor(buckets));
  const out = new Float32Array(n);
  const per = samples.length / n;
  let max = 0;
  for (let b = 0; b < n; b++) {
    let p = 0;
    const from = Math.floor(b * per);
    const to = Math.max(from + 1, Math.floor((b + 1) * per));
    for (let i = from; i < to && i < samples.length; i++) p = Math.max(p, Math.abs(samples[i]!));
    out[b] = p;
    max = Math.max(max, p);
  }
  if (max > 0) for (let b = 0; b < n; b++) out[b] = out[b]! / max;
  return out;
}
